import { legendSamples } from '../../composition/legendSamples';
import { useMemo, useRef, useState, type DragEvent as ReactDragEvent } from 'react';

import { Icon, type IconName } from '../../components/Icon';
import { ConfirmationDialog } from '../../components/ConfirmationDialog';
import type { VectorSourceDescriptor } from '../../data/model';
import type { LayerDefinition } from '../../project/model';
import { effectiveSymbols } from '../../styles/effectiveSymbols';
import { symbolGeometryForSource } from '../../styles/geometry';
import { deriveLegendEntries } from '../../styles/legend';
import { AnchoredMenu } from './AnchoredMenu';
import { focusAfterListItemRemoval } from './focusAfterRemoval';
import { SymbolPreview } from './style/SymbolPreview';

export type LayerDropPosition = 'after' | 'before' | null;

interface LayerRowProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
  readonly selected: boolean;
  readonly selectable: boolean;
  readonly symbolsExpandable: boolean;
  readonly removable: boolean;
  readonly reorderable: boolean;
  readonly expanded: boolean;
  readonly first: boolean;
  readonly last: boolean;
  readonly dropPosition: LayerDropPosition;
  readonly onDragEnd: () => void;
  readonly onDragOver: (event: ReactDragEvent<HTMLLIElement>) => void;
  readonly onDragStart: (event: ReactDragEvent<HTMLSpanElement>) => void;
  readonly onDrop: (event: ReactDragEvent<HTMLLIElement>) => void;
  readonly onExport: () => void;
  readonly onMoveDown: () => void;
  readonly onMoveUp: () => void;
  readonly onRemove: () => void;
  readonly onReplace: (returnFocusTo: HTMLElement | null) => void;
  readonly onRename: (name: string) => boolean;
  readonly onSelect: () => void;
  readonly onToggleExpanded: () => void;
  readonly onToggleVisibility: () => void;
  readonly onViewData: () => void;
  readonly onZoom: () => void;
  readonly exportAvailable: boolean;
  readonly viewDataAvailable: boolean;
  readonly readOnly?: boolean;
  readonly renameAvailable?: boolean;
  readonly replacementAvailable?: boolean;
  readonly replacementVisible?: boolean;
}

interface LayerMenuAction {
  readonly id:
    | 'export-data'
    | 'move-down'
    | 'move-up'
    | 'remove'
    | 'replace-data'
    | 'rename'
    | 'toggle-visibility'
    | 'view-data'
    | 'zoom';
  readonly label: string;
  readonly icon: IconName;
  readonly danger?: boolean;
  readonly disabled?: boolean;
  readonly run: () => void;
}

function swatchSymbols(layer: LayerDefinition) {
  const classification = layer.style.classification;
  const definitions =
    classification.mode === 'categorised'
      ? classification.categories.map((category) => category.symbol)
      : classification.mode === 'graduated'
        ? classification.classes.map((numericClass) => numericClass.symbol)
        : [];
  return definitions.length === 0
    ? layer.style.symbols
    : definitions
        .slice(0, 4)
        .map((definition) => effectiveSymbols(layer.style.symbols, definition));
}

export function LayerRow({
  layer,
  source,
  selected,
  selectable,
  symbolsExpandable,
  removable,
  reorderable,
  expanded,
  first,
  last,
  dropPosition,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onExport,
  onMoveDown,
  onMoveUp,
  onRemove,
  onReplace,
  onRename,
  onSelect,
  onToggleExpanded,
  onToggleVisibility,
  onViewData,
  onZoom,
  exportAvailable,
  viewDataAvailable,
  readOnly = false,
  renameAvailable = false,
  replacementAvailable = false,
  replacementVisible = false,
}: LayerRowProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [removeDialogReturnFocus, setRemoveDialogReturnFocus] = useState<HTMLElement | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(layer.name);
  const [renameError, setRenameError] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const rowRef = useRef<HTMLLIElement | null>(null);
  const kind = symbolGeometryForSource(source);
  const thematic = layer.style.classification.mode !== 'single';
  const legendEntries = useMemo(
    () => (thematic ? deriveLegendEntries(layer.name, layer.style) : []),
    [layer.name, layer.style, thematic],
  );
  const samples = useMemo(
    () =>
      legendSamples({
        layerId: layer.id,
        name: layer.name,
        headingEnabled: false,
        geometry: kind,
        entries: legendEntries,
      }),
    [layer.id, layer.name, kind, legendEntries],
  );
  const legendId = `layer-${layer.id}-symbols`;

  const renameAction: LayerMenuAction = {
    id: 'rename',
    label: 'Rename layer',
    icon: 'rename',
    run: () => {
      setRenameDraft(layer.name);
      setRenameError(false);
      setIsRenaming(true);
    },
  };
  const editableActions: readonly LayerMenuAction[] = [
    { id: 'zoom', label: 'Zoom to layer', icon: 'zoom', run: onZoom },
    ...(renameAvailable ? [renameAction] : []),
    ...(viewDataAvailable
      ? [
          {
            id: 'view-data' as const,
            label: 'View data',
            icon: 'table' as const,
            run: onViewData,
          },
        ]
      : []),
    ...(replacementVisible
      ? [
          {
            id: 'replace-data' as const,
            label: replacementAvailable ? 'Replace data' : 'Replace data — Pro',
            icon: replacementAvailable ? ('upload' as const) : ('lock' as const),
            disabled: !replacementAvailable,
            run: () => undefined,
          },
        ]
      : []),
    ...(exportAvailable
      ? [
          {
            id: 'export-data' as const,
            label: 'Export data…',
            icon: 'download' as const,
            run: onExport,
          },
        ]
      : []),
    ...(reorderable
      ? [
          {
            id: 'move-up' as const,
            label: 'Move up',
            icon: 'arrow-up' as const,
            disabled: first,
            run: onMoveUp,
          },
          {
            id: 'move-down' as const,
            label: 'Move down',
            icon: 'arrow-down' as const,
            disabled: last,
            run: onMoveDown,
          },
        ]
      : []),
    {
      id: 'toggle-visibility',
      label: layer.visible ? 'Hide layer' : 'Show layer',
      icon: layer.visible ? 'eye-off' : 'eye',
      run: onToggleVisibility,
    },
    ...(removable
      ? [
          {
            id: 'remove' as const,
            label: 'Remove layer',
            icon: 'trash' as const,
            danger: true,
            run: () => undefined,
          },
        ]
      : []),
  ];
  const actions = readOnly ? (renameAvailable ? [renameAction] : []) : editableActions;

  const mainContent = (
    <>
      <span
        aria-hidden="true"
        className={`layer-swatch layer-swatch-${kind}${thematic ? ' layer-swatch-thematic' : ''}`}
      >
        <SymbolPreview geometry={kind} symbols={swatchSymbols(layer)} />
      </span>
      <span className="layer-name" title={layer.name}>
        {layer.name}
      </span>
    </>
  );

  return (
    <li
      className={`layer-item${dropPosition ? ` layer-drop-${dropPosition}` : ''}`}
      data-layer-id={layer.id}
      onDragOver={onDragOver}
      onDrop={onDrop}
      ref={rowRef}
    >
      <div
        className={`layer-row${selected ? ' layer-row-selected' : ''}${reorderable ? ' layer-row-reorderable' : ''}`}
      >
        {reorderable ? (
          <span
            aria-hidden="true"
            className="layer-drag-handle"
            draggable
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            title={`Drag ${layer.name} to reorder`}
          >
            <Icon name="drag" />
          </span>
        ) : (
          <span aria-hidden="true" />
        )}
        <button
          aria-label={`${layer.visible ? 'Hide' : 'Show'} ${layer.name}`}
          aria-pressed={layer.visible}
          className="icon-button layer-visibility"
          onClick={onToggleVisibility}
          title={`${layer.visible ? 'Hide' : 'Show'} layer`}
          type="button"
        >
          <Icon name={layer.visible ? 'eye' : 'eye-off'} />
        </button>
        {symbolsExpandable ? (
          thematic ? (
            <button
              aria-controls={legendId}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Hide' : 'Show'} symbols for ${layer.name}`}
              className="icon-button layer-disclosure"
              onClick={onToggleExpanded}
              title={`${expanded ? 'Collapse' : 'Expand'} symbology`}
              type="button"
            >
              <Icon name={expanded ? 'chevron-down' : 'chevron-right'} />
            </button>
          ) : (
            <span aria-hidden="true" className="layer-disclosure-spacer" />
          )
        ) : (
          <span aria-hidden="true" className="layer-disclosure-spacer" />
        )}
        {isRenaming ? (
          <form
            className="layer-rename-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (onRename(renameDraft)) {
                setIsRenaming(false);
                setRenameError(false);
              } else setRenameError(true);
            }}
          >
            <label className="visually-hidden" htmlFor={`rename-${layer.id}`}>
              New name for {layer.name}
            </label>
            <input
              aria-describedby={renameError ? `rename-${layer.id}-error` : undefined}
              aria-invalid={renameError}
              autoFocus
              id={`rename-${layer.id}`}
              maxLength={120}
              onChange={(event) => {
                setRenameDraft(event.target.value);
                setRenameError(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setIsRenaming(false);
              }}
              value={renameDraft}
            />
            {renameError ? (
              <span className="visually-hidden" id={`rename-${layer.id}-error`} role="alert">
                Enter a unique, non-empty layer name.
              </span>
            ) : null}
            <button aria-label="Save layer name" className="icon-button" type="submit">
              <Icon name="check" />
            </button>
            <button
              aria-label="Cancel layer rename"
              className="icon-button"
              onClick={() => setIsRenaming(false)}
              type="button"
            >
              <Icon name="close" />
            </button>
          </form>
        ) : selectable ? (
          <button
            aria-label={`Select layer ${layer.name}`}
            aria-pressed={selected}
            className="layer-row-main"
            data-list-item-primary="true"
            id={`layer-select-${layer.id}`}
            onClick={onSelect}
            type="button"
          >
            {mainContent}
          </button>
        ) : (
          <div className="layer-row-main layer-row-main-static">{mainContent}</div>
        )}
        {actions.length > 0 ? (
          <div className="layer-actions">
            <button
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
              aria-label={`Actions for ${layer.name}`}
              className="icon-button layer-actions-trigger"
              onClick={() => setIsMenuOpen((open) => !open)}
              ref={menuTriggerRef}
              title="Layer actions"
              type="button"
            >
              <Icon name="more" />
            </button>
            <AnchoredMenu
              className="row-actions-menu"
              anchorRef={menuTriggerRef}
              label={`Actions for ${layer.name}`}
              onDismiss={() => setIsMenuOpen(false)}
              open={isMenuOpen}
            >
              {actions.map((action) => (
                <button
                  className={action.danger ? 'menu-danger' : undefined}
                  disabled={action.disabled}
                  key={action.id}
                  onClick={() => {
                    setIsMenuOpen(false);
                    if (action.id === 'remove') {
                      if (menuTriggerRef.current)
                        setRemoveDialogReturnFocus(menuTriggerRef.current);
                    } else if (action.id === 'replace-data') {
                      onReplace(menuTriggerRef.current);
                    } else action.run();
                  }}
                  role="menuitem"
                  type="button"
                >
                  <Icon name={action.icon} />
                  {action.label}
                </button>
              ))}
            </AnchoredMenu>
          </div>
        ) : null}
      </div>
      {symbolsExpandable && thematic && expanded ? (
        <div aria-label={`Symbols for ${layer.name}`} className="layer-legend" id={legendId}>
          {legendEntries.map((entry, index) => (
            <div className="layer-legend-entry" key={entry.id}>
              <SymbolPreview
                geometry={kind}
                label={`${entry.label} symbol`}
                symbols={entry.symbols}
                sample={samples[index]!}
              />
              <span title={entry.label}>{entry.label}</span>
            </div>
          ))}
        </div>
      ) : null}
      {removeDialogReturnFocus ? (
        <ConfirmationDialog
          confirmLabel="Remove layer"
          confirmTone="danger"
          description={`“${layer.name}” will be removed from this map.`}
          onCancel={() => setRemoveDialogReturnFocus(null)}
          onConfirm={() => {
            focusAfterListItemRemoval(rowRef.current, onRemove);
            setRemoveDialogReturnFocus(null);
          }}
          returnFocusTo={removeDialogReturnFocus}
          title="Remove layer?"
        />
      ) : null}
    </li>
  );
}
